import { cn } from "@/lib/utils"

export interface BrandInitialMonogramProps {
  /** Brand display name. The first character is uppercased + rendered. */
  name: string
  /** Optional Tailwind size + extra classes (e.g. `"size-8"` or `"size-7"`). */
  className?: string
  /** Forwarded as `data-testid` so consumers can keep their existing testid contracts. */
  testId?: string
}

/**
 * BrandInitialMonogram — neutral square tile carrying a single uppercased
 * brand-initial letter.
 *
 * Used wherever a brand without an uploaded logo would otherwise have shown
 * the OKS mark (the **brand-chrome fallback bug** OKS-266 fixes — see PRD
 * §1 + UX Spec §2). The OKS mark must NEVER masquerade as a brand avatar;
 * Crew is OKS-branded, but the chrome surfaces the operating brand as
 * data, not theme tokens.
 *
 * Visual treatment (UX Spec §2 + TRD KD-6):
 *   - `bg-foreground/10` tile (subtle neutral against `bg-background`)
 *   - `text-foreground` letter, `font-medium`
 *   - `rounded-md` to match the brand-logo `<img>` it's replacing
 *   - `aria-hidden="true"` — the brand name accompanies it in the same
 *     surface and is the textual identity for screen readers.
 *
 * Per UX Spec Open Q-2, V1 ships a single neutral color. Per-brand
 * colored tile is deferred to a follow-up.
 */
export function BrandInitialMonogram({
  name,
  className,
  testId,
}: BrandInitialMonogramProps) {
  const initial = (name?.trim().charAt(0) || "?").toUpperCase()

  return (
    <span
      aria-hidden="true"
      data-testid={testId}
      className={cn(
        "bg-foreground/10 text-foreground inline-flex items-center justify-center rounded-md font-medium",
        className,
      )}
    >
      {initial}
    </span>
  )
}

export default BrandInitialMonogram
